
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { UserFormValues } from "./userFormSchema";
import { UserRole } from "@/hooks/useUserRoles";

export const useCreateUser = (onSuccess?: () => void) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const sendWelcomeEmail = async (name: string, email: string, password: string) => {
    try {
      const response = await supabase.functions.invoke("send-welcome-email", {
        body: {
          name,
          email,
          temporaryPassword: password,
          loginUrl: `${window.location.origin}/login`
        },
      });

      if (response.error) {
        console.error("Error sending welcome email:", response.error);
        toast({
          title: "Notificação",
          description: "Usuário criado, mas o email de boas-vindas não pôde ser enviado.",
          variant: "default",
        });
      }
    } catch (error) {
      console.error("Failed to send welcome email:", error);
    }
  };

  const handleSubmit = async (values: UserFormValues) => {
    setIsSubmitting(true);

    try {
      // Create the user in Supabase Auth
      const { data: userData, error: createError } = await supabase.auth.admin.createUser({
        email: values.email,
        password: values.password,
        email_confirm: true, // Auto-confirm email so user doesn't need to verify
        user_metadata: { name: values.name }
      });

      if (createError) throw createError;
      if (!userData?.user) throw new Error("Falha ao criar usuário");

      // Assign the selected role to the user
      const { error: roleError } = await supabase
        .from('user_roles')
        .insert({
          user_id: userData.user.id,
          role: values.role as UserRole
        });

      if (roleError) throw roleError;

      // Send welcome email if the option is selected
      if (values.sendWelcomeEmail) {
        await sendWelcomeEmail(values.name, values.email, values.password);
      }

      toast({
        title: "Usuário criado com sucesso",
        description: `${values.name} foi adicionado como ${values.role}`,
      });

      if (onSuccess) onSuccess();
    } catch (error: any) {
      console.error("Error creating user:", error);
      
      let errorMessage = "Não foi possível criar o usuário";
      
      if (error.message) {
        // Handle specific Supabase error messages
        if (error.message.includes("duplicate key")) {
          errorMessage = "Este email já está em uso";
        } else {
          errorMessage = error.message;
        }
      }
      
      toast({
        title: "Erro",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return { handleSubmit, isSubmitting };
};
